/**
 * Tile Calculator - Area/Quantity conversion for tile products
 *
 * Handles dual-input calculator with:
 * - Area (m²/LM) input ↔ Box/Piece quantity sync
 * - 10% waste checkbox
 * - Total price calculation
 * - Coverage info display
 */

class TileCalculator extends HTMLElement {
  constructor() {
    super();

    this.coveragePerUnit = parseFloat(this.dataset.coveragePerUnit) || 0;
    this.soldByBox = this.dataset.soldByBox === 'true';
    this.boxM2 = parseFloat(this.dataset.boxM2) || 0;
    this.boxPieces = parseInt(this.dataset.boxPieces, 10) || 0;
    this.coverages = parseFloat(this.dataset.coverages) || 0;
    this.variantPrice = parseInt(this.dataset.variantPrice, 10) || 0;
    this.areaLabel = this.dataset.areaLabel || 'm²';
    this.purchasableUnit = this.dataset.purchasableUnit || 'Box';
    this.coverageSummaryVerb = this.dataset.coverageSummaryVerb || 'covers';
    this.coverageSummaryUnitLabel = this.dataset.coverageSummaryUnitLabel || this.areaLabel;
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';

    this.areaInput = this.querySelector('[data-tile-area-input]');
    this.qtyInput = this.querySelector('[data-tile-qty-input]');
    this.wasteCheckbox = this.querySelector('[data-tile-waste-checkbox]');
    this.totalPriceEl = this.querySelector('[data-tile-total-price]');
    this.coverageInfoEl = this.querySelector('[data-tile-coverage-info]');
    this.qtyUnitLabelEl = this.querySelector('[data-tile-qty-unit-label]');
    this.coveragePerUnitPropertyInput = this.querySelector('[data-tile-property-coverage-per-unit]');
    this.totalCoveragePropertyInput = this.querySelector('[data-tile-property-total-coverage]');
    this.tileCalculationPropertyInput = this.querySelector('[data-tile-property-tile-calculation]');

    // Store the raw user-entered area (before waste/rounding)
    this.rawArea = parseFloat(this.areaInput.value) || this.coveragePerUnit;
  }

  connectedCallback() {
    if (!this.areaInput || !this.qtyInput) return;

    this.areaInput.addEventListener('blur', this.handleAreaBlur.bind(this));
    this.areaInput.addEventListener('keydown', this.handleAreaKeydown.bind(this));
    this.qtyInput.addEventListener('change', this.handleQtyChange.bind(this));
    this.qtyInput.addEventListener('input', this.handleQtyInput.bind(this));

    if (this.wasteCheckbox) {
      this.wasteCheckbox.addEventListener('change', this.handleWasteToggle.bind(this));
    }

    // Listen for variant changes to update price
    document.addEventListener('variant:change', this.handleVariantChange.bind(this));

    // Also listen for the theme's variant selection event pattern
    const variantSelection = document.querySelector('[data-variant-selection]');
    if (variantSelection) {
      variantSelection.addEventListener('change', () => {
        // Small delay to let variant selection complete
        setTimeout(() => this.checkVariantPrice(), 50);
      });
    }

    const initialQty = Math.max(1, Math.round(parseFloat(this.qtyInput.value) || 1));
    this.areaInput.value = this.formatArea(this.rawArea);
    this.updateQtyUnitLabel(initialQty);
    this.updateCoverageSummary(initialQty, false);
    this.updateLineItemProperties(initialQty, false);
  }

  /**
   * Handle area input blur - round up to nearest unit coverage and sync qty
   */
  handleAreaBlur() {
    this.rawArea = parseFloat(this.areaInput.value) || 0;
    this.syncQtyFromArea();
  }

  /**
   * Handle Enter key in area input
   */
  handleAreaKeydown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.rawArea = parseFloat(this.areaInput.value) || 0;
      this.syncQtyFromArea();
      this.areaInput.blur();
    }
  }

  /**
   * Handle qty input change - sync area display
   */
  handleQtyChange() {
    this.syncAreaFromQty();
  }

  /**
   * Handle qty input typing - sync area display in real-time
   */
  handleQtyInput() {
    this.syncAreaFromQty();
  }

  /**
   * Handle waste checkbox toggle - recalculate from stored raw area
   */
  handleWasteToggle() {
    this.syncQtyFromArea();
  }

  /**
   * Calculate qty from area input, applying waste if checked, rounding up
   */
  syncQtyFromArea() {
    let requestedArea = this.rawArea;

    if (requestedArea <= 0 || isNaN(requestedArea)) {
      requestedArea = this.coveragePerUnit;
      this.rawArea = requestedArea;
    }

    // Apply waste factor
    let effectiveArea = requestedArea;
    const wasteApplied = this.wasteCheckbox && this.wasteCheckbox.checked;
    if (wasteApplied) {
      effectiveArea = requestedArea * 1.10;
    }

    // Round up to nearest whole purchasable unit
    const qty = Math.max(1, Math.ceil(effectiveArea / this.coveragePerUnit));
    this.qtyInput.value = qty;

    // Only snap area display if waste is NOT applied
    // When waste is checked, keep the user-entered m² value unchanged
    if (!wasteApplied) {
      const actualArea = qty * this.coveragePerUnit;
      this.areaInput.value = this.formatArea(actualArea);
    }

    this.updateTotalPrice(qty);
    this.updateQtyUnitLabel(qty);
    this.updateCoverageSummary(qty, wasteApplied);
    this.updateLineItemProperties(qty, wasteApplied);
    this.triggerQuantityChange(qty);
  }

  /**
   * Calculate area from qty input
   */
  syncAreaFromQty() {
    const qty = Math.max(1, Math.round(parseFloat(this.qtyInput.value) || 1));
    this.qtyInput.value = qty;

    // Calculate corresponding area
    const area = qty * this.coveragePerUnit;
    this.areaInput.value = this.formatArea(area);
    this.rawArea = this.roundArea(area);

    const wasteApplied = this.wasteCheckbox && this.wasteCheckbox.checked;
    this.updateTotalPrice(qty);
    this.updateQtyUnitLabel(qty);
    this.updateCoverageSummary(qty, wasteApplied);
    this.updateLineItemProperties(qty, wasteApplied);
    this.triggerQuantityChange(qty);
  }

  /**
   * Round area to 4 decimal precision for calculations
   */
  roundArea(value) {
    return Math.round(value * 10000) / 10000;
  }

  /**
   * Format area/coverage values to up to 4 decimals (trim trailing zeros)
   */
  formatArea(value) {
    const fixed = this.roundArea(value).toFixed(4);
    return fixed
      .replace(/(\.\d*?[1-9])0+$/, '$1')
      .replace(/\.0+$/, '.0');
  }

  /**
   * Update the total price display
   */
  updateTotalPrice(qty) {
    const totalCents = qty * this.variantPrice;
    this.totalPriceEl.textContent = this.formatMoney(totalCents);
  }

  /**
   * Update the coverage summary dynamically
   */
  updateCoverageSummary(qty, wasteApplied) {
    const totalCoverage = this.formatArea(qty * this.coveragePerUnit);
    const unitLabel = this.getPluralizedUnitLabel(qty);

    let summaryText = `${qty} ${unitLabel} ${this.coverageSummaryVerb} ${totalCoverage} ${this.coverageSummaryUnitLabel}`;

    if (wasteApplied) {
      summaryText += ' (10% extra)';
    }

    this.coverageInfoEl.textContent = summaryText;
  }

  /**
   * Keep Shopify line item properties aligned with the calculator state.
   */
  updateLineItemProperties(qty, wasteApplied) {
    const singleUnit = this.getPluralizedUnitLabel(1);
    const quantityUnit = this.getPluralizedUnitLabel(qty);
    const requestedArea = this.getRequestedAreaForProperties();

    if (this.coveragePerUnitPropertyInput) {
      this.coveragePerUnitPropertyInput.value = `1 ${singleUnit} = ${this.formatArea(this.coveragePerUnit)} ${this.coverageSummaryUnitLabel}`;
    }

    if (this.totalCoveragePropertyInput) {
      this.totalCoveragePropertyInput.value = `${qty} ${quantityUnit} = ${this.formatArea(qty * this.coveragePerUnit)} ${this.coverageSummaryUnitLabel}`;
    }

    if (this.tileCalculationPropertyInput) {
      let calculation = `${this.formatArea(requestedArea)} ${this.coverageSummaryUnitLabel}`;

      if (wasteApplied) {
        calculation += ' + 10% wastage';
      }

      this.tileCalculationPropertyInput.value = calculation;
    }
  }

  /**
   * Use the customer-entered amount as the persisted calculation basis.
   */
  getRequestedAreaForProperties() {
    const area = parseFloat(this.rawArea);

    if (Number.isFinite(area) && area > 0) {
      return area;
    }

    return this.coveragePerUnit;
  }

  /**
   * Return singular/plural unit labels with basic irregular handling
   */
  getPluralizedUnitLabel(qty) {
    const unit = (this.purchasableUnit || '').toLowerCase();

    if (qty === 1) return unit;

    if (unit === 'box') return 'boxes';
    if (unit === 'piece') return 'pieces';
    if (unit === 'sheet') return 'sheets';
    if (unit === 'set') return 'sets';
    if (unit === 'length') return 'lengths';

    if (unit.endsWith('s')) return unit;

    return `${unit}s`;
  }

  /**
   * Update the right-side qty unit label (Box/Boxes, Piece/Pieces, etc.)
   */
  updateQtyUnitLabel(qty) {
    if (!this.qtyUnitLabelEl) return;
    const unit = this.getPluralizedUnitLabel(qty);
    this.qtyUnitLabelEl.textContent = unit.charAt(0).toUpperCase() + unit.slice(1);
  }

  /**
   * Format cents into a money string using Shopify's money format
   */
  formatMoney(cents) {
    const format = this.moneyFormat || '${{amount}}';

    const value = (cents / 100).toFixed(2);
    const parts = value.split('.');
    const dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const centsStr = parts[1];

    return format
      .replace(/\{\{\s*amount\s*\}\}/g, dollars + '.' + centsStr)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, dollars)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, dollars + '.' + centsStr)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, dollars);
  }

  /**
   * Handle variant change events - update the stored price
   */
  handleVariantChange(event) {
    if (event.detail && event.detail.variant) {
      this.variantPrice = event.detail.variant.price;
      const qty = parseInt(this.qtyInput.value, 10) || 1;
      this.updateTotalPrice(qty);
    }
  }

  /**
   * Check variant price from the selected option
   */
  checkVariantPrice() {
    const variantSelect = document.querySelector('[data-variants]');
    if (variantSelect && variantSelect.value) {
      // The price update will come through the variant:change event
    }
  }

  /**
   * Dispatch a quantity change event for any listeners
   */
  triggerQuantityChange(qty) {
    this.dispatchEvent(new CustomEvent('quantity:change', {
      bubbles: true,
      detail: { quantity: qty }
    }));
  }
}

function formatMoneyWithShopifyFormat(cents, moneyFormat) {
  const format = moneyFormat || '${{amount}}';
  const value = (cents / 100).toFixed(2);
  const parts = value.split('.');
  const dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const centsStr = parts[1];

  return format
    .replace(/\{\{\s*amount\s*\}\}/g, `${dollars}.${centsStr}`)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, dollars)
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, `${dollars}.${centsStr}`)
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, dollars);
}

function initInlineTotalPrices(root = document) {
  const blocks = root.querySelectorAll('[data-inline-total-price]');

  blocks.forEach((block) => {
    if (block.dataset.bound === 'true') return;
    block.dataset.bound = 'true';

    const form = block.closest('form');
    if (!form) return;

    const qtyInput = form.querySelector('[data-quantity-input]');
    const valueEl = block.querySelector('[data-inline-total-value]');
    if (!qtyInput || !valueEl) return;

    let variantPrice = parseInt(block.dataset.variantPrice || '0', 10) || 0;
    const moneyFormat = block.dataset.moneyFormat || '${{amount}}';

    const updateTotal = () => {
      const qty = Math.max(1, Math.round(parseFloat(qtyInput.value) || 1));
      valueEl.textContent = formatMoneyWithShopifyFormat(qty * variantPrice, moneyFormat);
    };

    qtyInput.addEventListener('input', updateTotal);
    qtyInput.addEventListener('change', updateTotal);

    form.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('[data-quantity-plus], [data-quantity-minus]')) return;
      setTimeout(updateTotal, 0);
    });

    document.addEventListener('variant:change', (event) => {
      if (!form.contains(event.target)) return;
      if (!event.detail || !event.detail.variant) return;

      variantPrice = event.detail.variant.price || 0;
      block.dataset.variantPrice = String(variantPrice);
      updateTotal();
    });

    updateTotal();
  });
}

function initRandomSizeDisplay(root = document) {
  const forms = root.querySelectorAll('[data-product-form]');

  forms.forEach((form) => {
    const unitNode = form.querySelector('[data-product-unit-code]');
    if (!unitNode) return;

    const unitCode = (unitNode.textContent || '').trim().toUpperCase();
    if (unitCode !== 'RANDOM') return;

    const rows = document.querySelectorAll('.gs-spec-row');
    rows.forEach((row) => {
      const labelEl = row.querySelector('.gs-spec-label');
      const valueEl = row.querySelector('.gs-spec-value');
      if (!labelEl || !valueEl) return;

      const label = labelEl.textContent.replace(/\s+/g, ' ').trim().toLowerCase();
      if (label !== 'size:') return;

      valueEl.textContent = valueEl.textContent.replace(/\s*mm\s*$/i, '').trim();
    });
  });
}

function initProductPageEnhancements(root = document) {
  initInlineTotalPrices(root);
  initRandomSizeDisplay(root);
}

if (!customElements.get('tile-calculator')) {
  customElements.define('tile-calculator', TileCalculator);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initProductPageEnhancements(document));
} else {
  initProductPageEnhancements(document);
}

document.addEventListener('shopify:section:load', (event) => {
  initProductPageEnhancements(event.target || document);
});
